/**
 * Handles the Stripe Checkout redirect landing on Settings.
 *
 * Stripe's success/cancel redirect URLs (STRIPE_SUCCESS_URL / STRIPE_CANCEL_URL,
 * see docs/billing/stripe.md) send the user back to `/settings?billing=success`
 * or `?billing=cancel`. On success the server-side grant (subscription/credits)
 * has just changed, but the billing cache has a multi-minute TTL, so a plain
 * refresh is suppressed and the user would see stale state. This hook force-
 * refreshes past the TTL gate, surfaces a toast, and clears the query param so
 * a page reload does not re-fire the handler.
 */

import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBilling } from "@/contexts/useBilling";
import { notifyInfo, notifySuccess } from "@/lib/toast";

export function useCheckoutReturn(): void {
  const { t } = useTranslation(["billing"]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshAccount, refreshCredits } = useBilling();

  // Guard against React StrictMode's double-invoke (and any re-render before the
  // param is cleared) firing the toast/refresh twice.
  const handledRef = useRef(false);

  const billingParam = searchParams.get("billing");

  useEffect(() => {
    if (!billingParam || handledRef.current) return;
    if (billingParam !== "success" && billingParam !== "cancel") return;

    handledRef.current = true;

    if (billingParam === "success") {
      // Force past the TTL gate: the grant changed server-side moments ago.
      void refreshAccount(true);
      void refreshCredits(true);
      notifySuccess(
        t?.("billing:checkoutReturn.success") ??
          "Payment successful. Your account has been updated.",
      );
    } else {
      notifyInfo(
        t?.("billing:checkoutReturn.canceled") ??
          "Checkout canceled. You have not been charged.",
      );
    }

    // Strip only the `billing` param, preserving anything else on the URL.
    const next = new URLSearchParams(searchParams);
    next.delete("billing");
    setSearchParams(next, { replace: true });
  }, [
    billingParam,
    refreshAccount,
    refreshCredits,
    searchParams,
    setSearchParams,
    t,
  ]);
}
